/**
 * Messages d'erreur en français pour l'interface utilisateur.
 * Ton : tutoiement, phrases courtes, pas de jargon technique.
 * Chaque erreur propose une action ou une piste de solution.
 */

/** Types d'erreur reconnus */
export const ERROR_TYPES = {
  CONNECTION: 'connection',
  SERVER_SLOW: 'server_slow',
  REQUIRED_FIELD: 'required_field',
  INVALID_FILE: 'invalid_file',
  KYC_INCOMPLETE: 'kyc_incomplete',
  PAYMENT: 'payment',
  VALIDATION: 'validation',
  SESSION: 'session',
  FORBIDDEN: 'forbidden',
  DUO_EXISTS: 'duo_exists',
  RATE_LIMIT: 'rate_limit',
  GENERIC: 'generic',
};

/**
 * Messages utilisateur (titre, message, libellé du bouton principal).
 * @typedef {{ title: string, message: string, confirmLabel?: string, icon?: string }} UserError
 */

/** Catalogue des messages en français */
export const USER_ERRORS = {
  [ERROR_TYPES.CONNECTION]: {
    title: 'Problème de connexion',
    message: 'Vérifie ta connexion internet et réessaie.',
    confirmLabel: 'Réessayer',
    icon: 'warning',
  },
  [ERROR_TYPES.SERVER_SLOW]: {
    title: 'Le serveur met du temps à répondre',
    // Générique à dessein : ce type est déclenché depuis des pages très différentes
    // (formulaire, actions, résultats, connexion...), pas seulement à l'envoi d'un
    // formulaire — un message trop spécifique induirait en erreur ailleurs.
    message: 'Réessaie dans quelques instants. Si le problème persiste, contacte le support.',
    confirmLabel: 'OK',
    icon: 'warning',
  },
  [ERROR_TYPES.RATE_LIMIT]: {
    title: 'Trop de tentatives',
    message: 'Patiente quelques instants avant de réessayer.',
    confirmLabel: 'OK',
    icon: 'warning',
  },
  [ERROR_TYPES.REQUIRED_FIELD]: {
    title: 'Information manquante',
    message: 'Un champ obligatoire n’est pas rempli. Complète les informations demandées pour continuer.',
    confirmLabel: 'OK',
    icon: 'warning',
  },
  [ERROR_TYPES.INVALID_FILE]: {
    title: 'Format non accepté',
    message: 'Format autorisé : PDF, JPG, PNG (max 5 Mo).',
    confirmLabel: 'Sélectionner un autre fichier',
    icon: 'warning',
  },
  [ERROR_TYPES.KYC_INCOMPLETE]: {
    title: 'Dossier incomplet',
    message: 'Il manque des documents pour valider le client.',
    confirmLabel: 'Voir les documents manquants',
    icon: 'warning',
  },
  [ERROR_TYPES.PAYMENT]: {
    title: 'Paiement échoué',
    message: 'Le lien de paiement n’a pas pu être envoyé. Réessaie ou contacte le support.',
    confirmLabel: 'Réessayer',
    icon: 'error',
  },
  [ERROR_TYPES.VALIDATION]: {
    title: 'Informations à corriger',
    message: 'Certaines informations sont incorrectes ou manquantes. Corrige les champs indiqués puis réessaie.',
    confirmLabel: 'OK',
    icon: 'info',
  },
  [ERROR_TYPES.SESSION]: {
    title: 'Session expirée',
    message: 'Tu dois te reconnecter pour continuer.',
    confirmLabel: 'Se connecter',
    icon: 'warning',
  },
  [ERROR_TYPES.FORBIDDEN]: {
    title: 'Accès refusé',
    message: 'Tu n’as pas les droits pour effectuer cette action. Contacte un administrateur si besoin.',
    confirmLabel: 'OK',
    icon: 'warning',
  },
  [ERROR_TYPES.DUO_EXISTS]: {
    title: 'Analyse déjà enregistrée',
    message:
      'Une analyse a déjà été enregistrée. Consulte l’onglet Résultats d’analyses ou le tableau de bord.',
    confirmLabel: 'OK',
    icon: 'info',
  },
  [ERROR_TYPES.GENERIC]: {
    title: 'Oups ! Un problème est survenu',
    message: 'On a du mal à traiter ta demande. Recharge la page ou réessaie dans quelques minutes.',
    confirmLabel: 'Réessayer',
    icon: 'error',
  },
};

const FIELD_LABELS = {
  email: 'Email',
  username: "Nom d'utilisateur",
  password: 'Mot de passe',
  password_confirm: 'Confirmation du mot de passe',
  first_name: 'Prénom',
  last_name: 'Nom',
  adresse: 'Adresse',
  fonction: 'Fonction',
  tel: 'Numéro de téléphone',
  type_client: 'Type de client',
  role: 'Rôle',
  role_employe: "Rôle de l'employé",
  non_field_errors: 'Informations',
  detail: 'Informations',
};

function prettifyFieldName(name) {
  if (!name) return 'Informations';
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  return name
    .split('›')
    .map((part) => part.trim())
    .map((part) => FIELD_LABELS[part] || part.replace(/_/g, ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' > ');
}

function normalizeDetail(data) {
  const d = data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === 'string' ? x : x != null ? String(x) : ''))
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

/**
 * Détecte le type d'erreur à partir d'une erreur axios / fetch.
 * @param {object} error - Objet erreur (ex. error.response, error.code)
 * @returns {string} - Clé ERROR_TYPES
 */
export function getErrorType(error) {
  if (!error) return ERROR_TYPES.GENERIC;
  const code = error.code;
  const status = error.response?.status;
  const data = error.response?.data;
  const detail = normalizeDetail(data);

  if (code === 'ECONNABORTED' || (status >= 500 && status < 600)) {
    return ERROR_TYPES.SERVER_SLOW;
  }
  if (code === 'ERR_NETWORK') {
    return ERROR_TYPES.CONNECTION;
  }
  if (status === 429) return ERROR_TYPES.RATE_LIMIT;
  if (status === 401) return ERROR_TYPES.SESSION;
  if (status === 403 && (detail.toLowerCase().includes('kyc') || detail.toLowerCase().includes('document'))) {
    return ERROR_TYPES.KYC_INCOMPLETE;
  }
  if (status === 403) return ERROR_TYPES.FORBIDDEN;
  if (status === 400 && detail.toLowerCase().includes('existe déjà')) return ERROR_TYPES.DUO_EXISTS;
  if (status === 400 && Array.isArray(data) && data.length) return ERROR_TYPES.VALIDATION;
  if (status === 400 && data && typeof data === 'object' && !Array.isArray(data)) {
    const keys = Object.keys(data).filter((k) => !['detail', 'error', 'code', 'success'].includes(k));
    if (keys.length > 0) return ERROR_TYPES.VALIDATION;
    if (detail) return ERROR_TYPES.VALIDATION;
    if (typeof data.error === 'string' && data.error) return ERROR_TYPES.VALIDATION;
  }
  if (status === 400) return ERROR_TYPES.REQUIRED_FIELD;
  if (status === 402 || (status >= 400 && detail.toLowerCase().includes('paiement'))) {
    return ERROR_TYPES.PAYMENT;
  }
  return ERROR_TYPES.GENERIC;
}

/**
 * Retourne le message utilisateur (en français) pour une erreur.
 * Pour les erreurs de validation (400 avec champs), message peut être enrichi avec les champs.
 * @param {object} error - Erreur axios / fetch
 * @param {object} options - { validationMessages: string[] } pour injecter le détail des champs
 * @returns {UserError} - { title, message, confirmLabel, icon }
 */
export function getErrorMessage(error, options = {}) {
  const type = getErrorType(error);
  const base = USER_ERRORS[type] || USER_ERRORS[ERROR_TYPES.GENERIC];
  const out = { ...base };
  const derivedValidationMessages =
    options.validationMessages?.length ? options.validationMessages : formatValidationErrors(error?.response?.data);

  if (type === ERROR_TYPES.VALIDATION && derivedValidationMessages.length) {
    out.message = derivedValidationMessages.join('\n');
    if (out.message.length > 400) {
      out.message = derivedValidationMessages.slice(0, 5).join('\n') + '\n… (et d’autres champs)';
    }
  } else if (type === ERROR_TYPES.VALIDATION) {
    const detail = normalizeDetail(error?.response?.data);
    const errText = typeof error?.response?.data?.error === 'string' ? error.response.data.error.trim() : '';
    if (errText || detail) {
      out.message = errText && detail && detail !== errText ? `${errText}\n${detail}` : (errText || detail);
    }
  } else if (type === ERROR_TYPES.REQUIRED_FIELD) {
    const detail = normalizeDetail(error?.response?.data) || error?.response?.data?.error;
    if (typeof detail === 'string' && detail.length < 250) {
      out.message = detail;
    }
  } else if (type === ERROR_TYPES.GENERIC) {
    const detail = normalizeDetail(error?.response?.data);
    if (detail && detail.length < 200) {
      out.message = detail;
    } else if (typeof error?.response?.data?.error === 'string') {
      const d = error.response.data.error;
      if (d.length < 200) {
        out.message = d;
      }
    }
  }

  return out;
}

/**
 * Message garanti "propre" pour un echec reseau/API — jamais de texte brut.
 *
 * Remplace le pattern `error.response?.data?.detail || error.response?.data?.error
 * || error.message || 'repli'` repere dans une trentaine d'endroits du code : ce
 * pattern affiche le texte du serveur des qu'il existe, meme quand ce texte est
 * technique, en anglais, ou pense pour un developpeur (ex. "Backend injoignable.
 * Verifiez que le proxy vue.config.js pointe vers le bon port.").
 *
 * Les erreurs reconnues (session expiree, pas de connexion, trop de tentatives,
 * validation de champs...) gardent leur message du catalogue central. Pour tout le
 * reste (erreur serveur non categorisee), `safeErrorMessage` ignore le texte du
 * serveur et renvoie systematiquement `fallback` — a l'appelant de fournir une
 * phrase adaptee a son propre contexte ("Le rapport n'a pas pu etre genere.",
 * "Le controle n'a pas pu aboutir.", etc.).
 *
 * @param {object} error - Erreur axios / fetch
 * @param {string} [fallback] - Message de repli specifique au contexte d'appel
 * @returns {string} Message pret a afficher, jamais de texte brut serveur/tiers/JS
 */
export function safeErrorMessage(error, fallback) {
  const type = getErrorType(error);
  if (type === ERROR_TYPES.GENERIC) {
    return fallback || USER_ERRORS[ERROR_TYPES.GENERIC].message;
  }
  return getErrorMessage(error).message;
}

/**
 * Formate les erreurs de validation backend (objets imbriqués) en liste de chaînes lisibles.
 * @param {object} data - error.response.data
 * @returns {string[]}
 */
export function formatValidationErrors(data) {
  if (data == null) return [];
  if (typeof data === 'string') return [data];
  if (Array.isArray(data)) return data.map((m) => (typeof m === 'string' ? m : String(m)));

  const flatten = (obj, prefix = '') => {
    const acc = [];
    for (const [k, v] of Object.entries(obj)) {
      if (['success', 'code', 'detail', 'meta', 'error'].includes(k)) continue;
      const key = prefix ? `${prefix} › ${k}` : k;
      if (v == null) continue;
      if (typeof v === 'string') acc.push(`${prettifyFieldName(key)} : ${v}`);
      else if (Array.isArray(v)) v.forEach((m) => acc.push(`${prettifyFieldName(key)} : ${m}`));
      else if (typeof v === 'object') acc.push(...flatten(v, key));
      else acc.push(`${prettifyFieldName(key)} : ${v}`);
    }
    return acc;
  };

  const raw = flatten(data);
  if (!raw.length) {
    const detail = normalizeDetail(data);
    const err = typeof data?.error === 'string' ? data.error.trim() : '';
    if (err && detail && detail !== err) {
      raw.push(`${err} (${detail})`);
    } else if (err) {
      raw.push(err);
    } else if (detail) {
      raw.push(detail);
    }
  }
  return raw.map((line) => line.replace(/^Informations\s:\s/i, ''));
}

/** Codes d'erreur renvoyés par DELETE /api/admin/clients/:login/delete/ */
export const ADMIN_CLIENT_DELETE_CODES = {
  DEL_CLIENT_FORBIDDEN: 'Accès refusé',
  DEL_CLIENT_NOT_FOUND: 'Client introuvable',
  DEL_CLIENT_SELF_DELETE: 'Auto-suppression interdite',
  DEL_CLIENT_SCHEMA_MISMATCH: 'Schéma base incomplet',
  DEL_CLIENT_FK_BLOCKED: 'Données liées en base',
  DEL_CLIENT_INTEGRITY: 'Contrainte d\'intégrité',
  DEL_CLIENT_DB_ERROR: 'Erreur base de données',
  DEL_CLIENT_LOOKUP_FAILED: 'Échec chargement client',
  DEL_CLIENT_PURGE_FAILED: 'Échec purge analyses',
  DEL_CLIENT_PROFILE_FAILED: 'Échec suppression profil client',
  DEL_CLIENT_DELETE_USER_FAILED: 'Échec suppression compte',
  DEL_CLIENT_ORPHAN_FAILED: 'Échec profil orphelin',
  DEL_CLIENT_UNEXPECTED: 'Erreur serveur inattendue',
  DEL_CLIENT_UNHANDLED: 'Erreur non interceptée (handler500)',
  DEL_CLIENT_RESPONSE_BUILD_FAILED: 'Échec construction réponse erreur',
  DEL_CLIENT_HTML_500: 'Page HTML 500 (backend non à jour)',
  API_ERROR_HANDLER_FAILED: 'Gestionnaire d\'erreurs API manquant',
  API_HANDLER500: 'Erreur serveur Django (handler500)',
  API_UNHANDLED_ERROR: 'Erreur API non gérée',
  API_INTEGRITY_ERROR: 'Contrainte base de données',
  API_DB_ERROR: 'Erreur base de données',
  API_DB_UNAVAILABLE: 'Base indisponible',
  API_SCHEMA_MISMATCH: 'Migrations manquantes',
};

/**
 * Corps du message (detail + hint), sans répéter le code (affiché séparément dans l'UI).
 * @param {object} data - error.response.data
 * @returns {string}
 */
export function formatAdminClientDeleteBody(data) {
  if (!data || typeof data !== 'object') return '';

  const detail = normalizeDetail(data);
  const hint = typeof data.hint === 'string' ? data.hint.trim() : '';

  const parts = [];
  if (detail) parts.push(detail);
  if (hint) parts.push(hint);

  return parts.join(' ');
}

/**
 * Formate l'erreur de suppression admin client pour affichage UI (+ console support).
 * L'interface doit rester compréhensible pour un utilisateur : pas de codes
 * internes, pas de détails techniques ni de noms de fichiers backend.
 * @param {object} error - Erreur axios
 * @param {{ includeCode?: boolean }} [options]
 * @returns {string}
 */
export function formatAdminClientDeleteError(error, options = {}) {
  const { includeCode = true } = options;
  const status = error?.response?.status;
  const raw = error?.response?.data;
  const data = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};

  if (typeof raw === 'string' && /server error|html|doctype/i.test(raw)) {
    return 'La suppression a échoué. Réessayez dans quelques instants ou contactez le support.';
  }

  const parts = [];
  const body = formatAdminClientDeleteBody(data);
  if (body) parts.push(body);

  if (!parts.length) {
    if (error?.code === 'ECONNABORTED') {
      return 'La suppression a pris trop de temps. Réessayez dans quelques instants.';
    }
    if (error?.code === 'ERR_NETWORK' || !error?.response) {
      return 'Un problème de connexion a empêché la suppression. Vérifie ta connexion puis réessaie.';
    }
    if (status === 403) {
      return 'Tu n’as pas les droits pour supprimer ce client.';
    }
    if (status === 404) {
      return 'Ce client est introuvable. Recharge la page puis réessaie.';
    }
    return 'La suppression du client a échoué. Réessayez dans quelques instants.';
  }

  if (includeCode && status && parts.length === 1 && /^HTTP \d+$/.test(parts[0])) {
    return 'La suppression du client a échoué. Réessayez dans quelques instants.';
  }

  return parts.join(' ');
}
